import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, StyleSheet, Text, View } from 'react-native';

import { testProps } from '../../../config';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { getSafeAreaSidePadding } from '../../../utils/safeArea';
import { CtaButton } from '../common/CtaButton';
import { TouchableOpacity } from '../common/Touchable';
import { redesignPanelColors, RedesignPanelFill, redesignPanelFrame } from './redesignPanelChrome';

const PANEL_RADIUS = 16;

interface ErrorModalProps {
	visible: boolean;
	title: string;
	message: string;
	onTryAgain?: () => void;
	tryAgainLabel?: string;
	onGoToLobby?: () => void;
	onCancel: () => void;
}

const ErrorFindTableModal: React.FC<ErrorModalProps> = ({
	visible,
	title,
	message,
	onTryAgain,
	tryAgainLabel,
	onGoToLobby,
	onCancel,
}) => {
	const { t } = useTranslation();
	const { insets } = useSafeViewport();
	const { isMobile } = useDevice();
	const safeOverlayInsets = getSafeAreaSidePadding(insets, 20);

	return (
		<Modal
			visible={visible}
			transparent
			animationType='fade'
			onRequestClose={onCancel}
			statusBarTranslucent
			navigationBarTranslucent
		>
			<View style={[styles.overlay, safeOverlayInsets]}>
				<TouchableOpacity activeOpacity={1} onPress={onCancel} style={styles.backdropTapArea} />
				<View style={[styles.modalContainer, isMobile && styles.modalMobile]}>
					<RedesignPanelFill radius={PANEL_RADIUS} />
					<Text style={styles.title}>{title}</Text>
					<Text style={styles.message}>{message}</Text>

					<View style={styles.buttonContainer}>
						{onGoToLobby && (
							<View {...testProps('cta-error-find-table-go-to-lobby')}>
								<CtaButton
									variant='gold'
									label={t('T_SITE_GO_TO_LOBBY', { defaultValue: 'Go to Lobby' })}
									onPress={onGoToLobby}
									style={styles.cta}
									labelStyle={styles.ctaLabel}
								/>
							</View>
						)}

						{onTryAgain && (
							<View {...testProps('cta-error-find-table-try-again')}>
								<CtaButton
									label={tryAgainLabel ?? t('T_RB_TryAgain', { defaultValue: 'Try Again' })}
									onPress={onTryAgain}
									style={styles.cta}
									labelStyle={styles.ctaLabel}
								/>
							</View>
						)}

						<TouchableOpacity
							style={[styles.button, styles.cancelButton]}
							onPress={onCancel}
							{...testProps('cta-error-find-table-cancel')}
						>
							<Text style={styles.buttonText}>
								{t('T_SITE_MODAL_NEW_ROOM_CANCEL', { defaultValue: 'Cancel' })}
							</Text>
						</TouchableOpacity>
					</View>
				</View>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	overlay: {
		flex: 1,
		backgroundColor: redesignPanelColors.backdrop,
		justifyContent: 'center',
		alignItems: 'center',
		padding: 20,
	},
	modalContainer: {
		...redesignPanelFrame(PANEL_RADIUS),
		padding: 28,
		width: '90%',
		maxWidth: 420,
	},
	backdropTapArea: {
		...StyleSheet.absoluteFill,
	},
	modalMobile: {
		width: '95%',
		padding: 20,
	},
	title: {
		fontFamily: 'Sofia Sans',
		fontSize: 24,
		fontWeight: '800',
		color: redesignPanelColors.title,
		marginBottom: 12,
		textAlign: 'center',
		textShadowColor: 'rgba(255, 114, 0, 0.6)',
		textShadowOffset: { width: 0, height: 0 },
		textShadowRadius: 6,
	},
	message: {
		fontFamily: 'Sofia Sans',
		fontSize: 16,
		fontWeight: '500',
		color: redesignPanelColors.white,
		marginBottom: 24,
		textAlign: 'center',
		lineHeight: 22,
	},
	buttonContainer: {
		flexDirection: 'column',
		gap: 10,
	},
	cta: {
		alignSelf: 'stretch',
		minHeight: 44,
	},
	ctaLabel: {
		fontSize: 16,
	},
	button: {
		paddingVertical: 12,
		paddingHorizontal: 20,
		borderRadius: 10,
		borderWidth: 1,
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: 44,
	},
	cancelButton: {
		backgroundColor: redesignPanelColors.tileFill,
		borderColor: redesignPanelColors.tileBorder,
	},
	buttonText: {
		fontFamily: 'Sofia Sans',
		color: redesignPanelColors.white,
		fontSize: 16,
		fontWeight: '700',
	},
});

export default ErrorFindTableModal;
